import React from 'react';
import { makeStyles, Typography, Grid } from '@material-ui/core';
import { useTranslation, TFunction } from 'react-i18next';
import { apiTranslate } from 'translations/translations';
import {
  FieldResponse,
  FieldTypeEnumResponse,
  PositionResponse,
  DeductionsResponse,
  FieldWithDeductionsResponse,
  FieldBooleanResponse,
  FieldWithManualAreaResponse,
  ManualAreaResponse,
  FieldWithKeyValuePairStringIntResponse,
  FieldWithOptionsResponse,
  FieldWithOptionsMultiSelectResponse,
  OptionResponse,
} from '.generated/api';

const useStyles = makeStyles((theme) => ({
  title: {
    textAlign: 'left',
    width: '100%',
    fontWeight: 'bold',
  },
  key: {
    width: '100%',
    textAlign: 'left',
    fontFamily: 'Kobenhavn',
    color: theme.palette.secondary.main,
    fontSize: '0.8rem',
  },
  value: {
    textAlign: 'right',
    fontWeight: 600,
    fontFamily: 'Kobenhavn',
    color: theme.palette.primary.main,
    fontSize: '1rem',
  },
  unit: {
    fontWeight: 'bold',
    textAlign: 'left',
  },
  image: {
    width: '100%',
  },
}));

function formatList(
  items: string[],
  classNameValue: string,
): React.ReactElement {
  return (
    <Grid container direction="row">
      {items.map((item, i) => (
        <Grid xs={12} key={i} item className={classNameValue}>
          {item}
        </Grid>
      ))}
    </Grid>
  );
}

function formatDoubleWithFixed(v: number, unit: string, fixed: number): string {
  return `${v.toFixed(fixed)} ${unit}`;
}

function formatDouble(v: number, unit: string): string {
  return formatDoubleWithFixed(v, unit, 3);
}

function formatDoubleArray(
  values: number[],
  unit: string,
  classNameValue: string,
): React.ReactElement {
  return formatList(
    values.map((v) => formatDouble(v, unit)),
    classNameValue,
  );
}

function formatIntArray(
  values: number[],
  unit: string,
  classNameValue: string,
): React.ReactElement {
  return formatList(
    values.map((v) => formatInteger(v, unit)),
    classNameValue,
  );
}

function formatBoolean(v: FieldBooleanResponse): string {
  if (v.defaultValue === true) {
    return apiTranslate(v.trueLabel ?? {});
  }
  if (v.defaultValue === false) {
    return apiTranslate(v.falseLabel ?? {});
  }
  return '';
}

function formatDate(v: string): string {
  const date = new Date(v);
  return date ? date.toLocaleDateString(navigator.language) : '';
}

function formatInteger(v: number, unit: string): string {
  return `${v} ${unit}`;
}

function formatMultipleOptions(
  selectedKeys: string[],
  options: OptionResponse[],
  classNameValue: string,
): React.ReactElement {
  const values = options
    .filter((option) => selectedKeys.includes(option.key))
    .map((option) => apiTranslate(option.text));
  return formatList(values, classNameValue);
}

function formatString(v: string): string {
  return v;
}

function formatText(v: string): string {
  return v;
}

function formatImage(value: string, className: string): React.ReactElement {
  return <img src={value} className={className} />;
}

function formatKeyValuePairStringInt(
  v: Record<string, number>,
  options: OptionResponse[],
  unit: string,
  classNameKey: string,
  classNameValue: string,
) {
  return (
    <>
      {Object.keys(v).map((key) => {
        const option = options.find((option) => option.key === key);
        const translatedValue = option ? apiTranslate(option.text) : '';
        return (
          <Grid
            container
            key={key}
            direction="row"
            alignItems="baseline"
            spacing={1}
          >
            <Grid item xs={6} className={classNameKey}>
              {formatString(translatedValue)}
            </Grid>
            <Grid item xs={6} className={classNameValue}>
              {formatInteger(v[key], unit)}
            </Grid>
          </Grid>
        );
      })}
    </>
  );
}

function formatDeductions(
  { deductionsCount, averageCount }: DeductionsResponse,
  keyUnit: string,
  classNameValue: string,
): React.ReactElement {
  const items: string[] = [];

  if (averageCount) {
    items.push(`Ø: ${averageCount}`);
  }

  if (deductionsCount) {
    for (const { item1, item2 } of deductionsCount) {
      items.push(`${item1} ${keyUnit}: ${item2}`);
    }
  }

  return formatList(items, classNameValue);
}

function formatPosition(
  { latitude, longitude, height, accuracy }: PositionResponse,
  classNameKey: string,
  classNameValue: string,
  t: TFunction<string[]>,
): React.ReactElement {
  return (
    <Grid container direction="row" alignItems="flex-start" spacing={1}>
      <Grid item xs={6} className={classNameKey}>
        {t('stack_page.latitude_abbrev')}:
      </Grid>
      <Grid item xs={6} className={classNameValue}>
        {latitude && latitude.toFixed(7)}
      </Grid>
      <Grid item xs={6} className={classNameKey}>
        {t('stack_page.longitude_abbrev')}:
      </Grid>
      <Grid item xs={6} className={classNameValue}>
        {longitude && longitude.toFixed(7)}
      </Grid>
      <Grid item xs={6} className={classNameKey}>
        {t('stack_page.accuracy')}:
      </Grid>
      <Grid item xs={6} className={classNameValue}>
        {accuracy && accuracy.toFixed(0)} m
      </Grid>
      <Grid item xs={6} className={classNameKey}>
        {t('stack_page.height')}:
      </Grid>
      <Grid item xs={6} className={classNameValue}>
        {height && height.toFixed(0)} m
      </Grid>
    </Grid>
  );
}

function formatManualArea(
  {
    sectionAHeights,
    sectionALength,
    sectionBHeights,
    sectionBLength,
  }: ManualAreaResponse,
  {
    sectionAHeightsTitle,
    sectionALengthTitle,
    sectionBHeightsTitle,
    sectionBLengthTitle,
  }: FieldWithManualAreaResponse,
  unit: string,
  classNameKey: string,
  classNameValue: string,
) {
  return (
    <Grid container direction="row" alignItems="flex-start" spacing={1}>
      {
        // section A
      }
      <Grid item xs={6} className={classNameKey}>
        {apiTranslate(sectionALengthTitle)}:
      </Grid>
      <Grid item xs={6} className={classNameValue}>
        {formatDoubleWithFixed(sectionALength, unit, 2)}
      </Grid>
      <Grid item xs={6} className={classNameKey}>
        {apiTranslate(sectionAHeightsTitle)}:
      </Grid>
      <Grid item xs={6} className={classNameValue}>
        {sectionAHeights && sectionAHeights.length > 0
          ? formatDoubleWithFixed(sectionAHeights[0], unit, 2)
          : null}
      </Grid>
      {sectionAHeights &&
        sectionAHeights.map((height, i) => {
          if (i === 0) {
            return null;
          }
          return (
            <React.Fragment key={i}>
              <Grid item xs={6} className={classNameKey} />
              <Grid item xs={6} className={classNameValue}>
                {formatDoubleWithFixed(height, unit, 2)}
              </Grid>
            </React.Fragment>
          );
        })}

      {
        // section B
      }
      <Grid item xs={6} className={classNameKey}>
        {apiTranslate(sectionBLengthTitle)}:
      </Grid>
      <Grid item xs={6} className={classNameValue}>
        {formatDoubleWithFixed(sectionBLength, unit, 2)}
      </Grid>

      <Grid item xs={6} className={classNameKey}>
        {apiTranslate(sectionBHeightsTitle)}:
      </Grid>
      <Grid item xs={6} className={classNameValue}>
        {formatDoubleWithFixed(sectionBHeights, unit, 2)}
      </Grid>
    </Grid>
  );
}

type FieldViewProps = {
  field: FieldResponse;
};

const FieldView = ({ field }: FieldViewProps): React.ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation(['translations']);
  let formattedValue;
  const unit = field.units?.display ? apiTranslate(field.units.display) : '';

  switch (field.fieldType) {
    case FieldTypeEnumResponse.DOUBLE:
      formattedValue = formatDouble(field.defaultValue, unit);
      break;
    case FieldTypeEnumResponse.ARRAY_OF_DOUBLE:
      formattedValue = formatDoubleArray(
        field.defaultValue,
        unit,
        classes.value,
      );
      break;
    case FieldTypeEnumResponse.ARRAY_OF_INT:
      formattedValue = formatIntArray(field.defaultValue, unit, classes.value);
      break;
    case FieldTypeEnumResponse.BOOLEAN:
      formattedValue = formatBoolean(field);
      break;
    case FieldTypeEnumResponse.DATE:
      formattedValue = formatDate(field.defaultValue);
      break;
    case FieldTypeEnumResponse.INTEGER:
      formattedValue = formatInteger(field.defaultValue, unit);
      break;
    case FieldTypeEnumResponse.MULTIPLE_OPTIONS: {
      const optionsField = field as FieldWithOptionsMultiSelectResponse;
      formattedValue = formatMultipleOptions(
        optionsField.defaultValue,
        optionsField.options ?? [],
        classes.value,
      );
      break;
    }
    case FieldTypeEnumResponse.STRING:
      formattedValue = formatString(field.defaultValue);
      break;
    case FieldTypeEnumResponse.TEXT:
      formattedValue = formatText(field.defaultValue);
      break;
    case FieldTypeEnumResponse.OPTIONS: {
      const optionsField = field as FieldWithOptionsResponse;
      const option = optionsField.options?.find(
        (option) => option.key === optionsField.defaultValue,
      );
      const translatedValue = option ? apiTranslate(option.text) : '';
      formattedValue = formatString(translatedValue);
      break;
    }
    case FieldTypeEnumResponse.KEY_VALUE_PAIR_STRING_INT: {
      const keyValuePairStringIntField =
        field as FieldWithKeyValuePairStringIntResponse;
      formattedValue = formatKeyValuePairStringInt(
        keyValuePairStringIntField.defaultValue,
        keyValuePairStringIntField.options ?? [],
        unit,
        classes.key,
        classes.value,
      );
      break;
    }
    case FieldTypeEnumResponse.IMAGE:
      formattedValue = formatImage(field.defaultValue, classes.image);
      break;
    case FieldTypeEnumResponse.POSITION:
      formattedValue = formatPosition(
        field.defaultValue,
        classes.key,
        classes.value,
        t,
      );
      break;
    case FieldTypeEnumResponse.DEDUCTIONS: {
      const deductionsField = field as FieldWithDeductionsResponse;
      const keyUnit = deductionsField.keyUnit?.display
        ? apiTranslate(deductionsField.keyUnit.display)
        : '';
      formattedValue = formatDeductions(
        field.defaultValue,
        keyUnit,
        classes.value,
      );
      break;
    }
    case FieldTypeEnumResponse.MANUAL_AREA:
      formattedValue = formatManualArea(
        field.defaultValue as ManualAreaResponse,
        field as FieldWithManualAreaResponse,
        unit,
        classes.key,
        classes.value,
      );
      break;
    default:
      formattedValue = 'Unexpected type: ' + field.fieldType;
      break;
  }

  return (
    <>
      <Grid item xs={6}>
        <Typography variant="subtitle1" className={classes.title}>
          {field.title ? apiTranslate(field.title) : null}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <div className={classes.value}>{formattedValue}</div>
      </Grid>
    </>
  );
};

export { FieldView };
